import React from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import colors from "../../assets/theme/colors";
import { finalizeImportHistoryReview, saveImportHistoryReviewDraft } from "./careSourceProcessingClient";
import { annotatePossibleImportOverlaps } from "../importHistory/importHistoryBatchModel";
import ImportHistoryReviewGroup from "./ImportHistoryReviewGroup";
import ImportHistoryReviewFileList from "./ImportHistoryReviewFileList";
import { getLogTypeByCategory, LOG_TYPES } from "../../constants/logTypeRegistry";
import MedicationSourceReview from "./MedicationSourceReview";
import { buildImportHistoryMedicationReviewSource } from "./importHistoryMedicationReview";

const getCandidateText = (candidate = {}) => {
  const suggestedRecord = candidate.suggestedRecord || {};
  const record = suggestedRecord.record || {};
  return String(record.text || record.note || record.summary || suggestedRecord.sourceText || "").trim();
};

const buildDraft = (candidate = {}, sourceNoteId = "", savedDecision = null) => {
  const suggestedRecord = candidate.suggestedRecord || {};
  const record = suggestedRecord.record || {};
  const edits = savedDecision?.edits || {};
  const dateYearAssumed = Boolean(record.metadata?.dateYearAssumed);
  return {
    key: candidate.key,
    sourceNoteId,
    include: savedDecision ? savedDecision.action === "approve" : Boolean(record.localDate) && !dateYearAssumed,
    title: edits.title ?? suggestedRecord.title ?? record.title ?? "",
    text: edits.text ?? getCandidateText(candidate),
    category: getLogTypeByCategory(edits.category || record.category || suggestedRecord.category || "log")?.id || LOG_TYPES.log.id,
    localDate: edits.localDate ?? record.localDate ?? "",
    localTime: edits.localTime ?? record.localTime ?? record.time ?? "",
    sourceText: suggestedRecord.sourceText || record.sourceText || "",
    dateYearAssumed,
    dateSourceLabel: record.metadata?.dateSourceLabel || record.displayDate || record.dateHint || "",
    dateConfirmed: savedDecision ? Boolean(savedDecision.dateConfirmed) : !dateYearAssumed,
  };
};

const buildReviewDecisions = (drafts = []) => drafts.map((draft) => ({
  key: draft.key,
  action: draft.include ? "approve" : "skip",
  dateConfirmed: Boolean(draft.dateConfirmed),
  edits: {
    title: draft.title,
    text: draft.text,
    category: draft.category,
    localDate: draft.localDate,
    localTime: draft.localTime,
  },
}));

const ImportHistoryReviewSheet = ({ source = null, sources = null, open = false, onClose, existingMedications = [], onMedicationListChanged }) => {
  const reviewSources = React.useMemo(() => (Array.isArray(sources) && sources.length ? sources : source ? [source] : []), [source, sources]);
  const groups = React.useMemo(
    () =>
      annotatePossibleImportOverlaps(
        reviewSources.map((item) => ({
          sourceNoteId: item.id,
          fileName: item.sourceMetadata?.fileName || item.metadata?.source?.fileName || "Imported document",
          source: item,
          candidates: item.pendingTimelineReview?.candidates || [],
        })),
      ),
    [reviewSources],
  );
  const candidates = React.useMemo(() => groups.flatMap((group) => group.candidates), [groups]);
  const [drafts, setDrafts] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [selectedSourceId, setSelectedSourceId] = React.useState("");
  const [reviewedSourceIds, setReviewedSourceIds] = React.useState(() => new Set());
  const [mobileDetailOpen, setMobileDetailOpen] = React.useState(false);
  const [selectedSourceContext, setSelectedSourceContext] = React.useState(null);
  const [medicationReviewSource, setMedicationReviewSource] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setDrafts(groups.flatMap((group) => {
      const savedDecisions = new Map((group.source.pendingTimelineReview?.draftReview?.decisions || []).map((decision) => [decision.key, decision]));
      return group.candidates.map((candidate) => buildDraft(candidate, group.sourceNoteId, savedDecisions.get(candidate.key)));
    }));
    setSelectedSourceId(groups[0]?.sourceNoteId || "");
    setReviewedSourceIds(new Set(groups.filter((group) => group.source.pendingTimelineReview?.draftReview?.reviewed).map((group) => group.sourceNoteId)));
    setMobileDetailOpen(false);
    setSelectedSourceContext(null);
    setMedicationReviewSource(null);
    setError("");
  }, [groups, open]);

  const updateDraft = (key, patch) => {
    setDrafts((current) => current.map((draft) => {
      if (draft.key !== key) return draft;
      const datePatch = Object.prototype.hasOwnProperty.call(patch, "localDate")
        ? { dateConfirmed: Boolean(patch.localDate) }
        : {};
      return { ...draft, ...patch, ...datePatch };
    }));
  };

  const notifyTimelineRefresh = (successfulSourceIds, savedCount) => {
    if (typeof window === "undefined") return;
    const successfulSourceIdSet = new Set(successfulSourceIds);
    const entryDates = Array.from(new Set(
      drafts
        .filter((draft) => successfulSourceIdSet.has(draft.sourceNoteId) && draft.include && draft.localDate)
        .map((draft) => draft.localDate),
    ));
    window.dispatchEvent(
      new CustomEvent("captureez:timeline-refresh", {
        detail: {
          childId: reviewSources[0]?.childId || null,
          count: savedCount,
          entryDates,
        },
      }),
    );
  };

  const handleSave = async () => {
    if (!reviewSources.some((item) => item.id) || saving) return;
    setSaving(true);
    setError("");
    try {
      const reviewedSources = reviewSources.filter((item) => item.id && reviewedSourceIds.has(item.id));
      const results = await Promise.allSettled(
        reviewedSources
          .map((item) =>
            finalizeImportHistoryReview({
              sourceNoteId: item.id,
              decisions: buildReviewDecisions(drafts.filter((draft) => draft.sourceNoteId === item.id)),
            }),
          ),
      );
      const successfulSourceIds = results.flatMap((result, index) => (
        result.status === "fulfilled" ? [reviewedSources[index].id] : []
      ));
      setReviewedSourceIds((current) => {
        const remaining = new Set(current);
        successfulSourceIds.forEach((sourceId) => remaining.delete(sourceId));
        return remaining;
      });
      const savedCount = results.reduce((total, result) => (
        result.status === "fulfilled" ? total + Number(result.value?.savedCount || 0) : total
      ), 0);
      if (successfulSourceIds.length) {
        notifyTimelineRefresh(successfulSourceIds, savedCount);
      }
      const failures = results.filter((result) => result.status === "rejected");
      if (failures.length) {
        setError(`${failures.length} document${failures.length === 1 ? "" : "s"} could not be saved. The others were added; retry only the remaining document groups.`);
        return;
      }
      if (reviewedSources.length === reviewSources.length) {
        onClose?.({ complete: true, savedCount, results: results.map((result) => result.value) });
      }
    } catch (saveError) {
      setError(saveError?.message || "We couldn't save these historical entries.");
    } finally {
      setSaving(false);
    }
  };

  const reviewedDrafts = drafts.filter((draft) => reviewedSourceIds.has(draft.sourceNoteId));
  const reviewedIncludedCount = reviewedDrafts.filter((draft) => draft.include).length;
  const includedMissingDate = drafts.some((draft) => draft.include && !draft.localDate);
  const missingDateCount = drafts.filter((draft) => !draft.localDate).length;
  const selectedGroup = groups.find((group) => group.sourceNoteId === selectedSourceId) || groups[0];
  const selectedDrafts = drafts.filter((draft) => draft.sourceNoteId === selectedGroup?.sourceNoteId);

  const selectSource = (sourceNoteId) => {
    setSelectedSourceId(sourceNoteId);
    setMobileDetailOpen(true);
  };

  const openMedicationReview = (candidate, draft) => {
    const reviewSource = buildImportHistoryMedicationReviewSource({
      source: selectedGroup?.source || {},
      candidate,
      draft,
    });
    if (!reviewSource) {
      setError("We found a medication change, but could not identify the medication name. Edit the entry or review it from Medications.");
      return;
    }
    setMedicationReviewSource(reviewSource);
  };

  const finishSelectedSource = async () => {
    if (!selectedGroup?.sourceNoteId) return;
    setSaving(true);
    setError("");
    try {
      await saveImportHistoryReviewDraft({
        sourceNoteId: selectedGroup.sourceNoteId,
        decisions: buildReviewDecisions(selectedDrafts),
        reviewed: true,
      });
      setReviewedSourceIds((current) => new Set([...current, selectedGroup.sourceNoteId]));
      setMobileDetailOpen(false);
    } catch (saveError) {
      setError(saveError?.message || "We couldn't save this review yet.");
    } finally {
      setSaving(false);
    }
  };

  const addSelectedSource = async () => {
    if (!selectedGroup?.sourceNoteId || saving) return;
    setSaving(true);
    setError("");
    try {
      const result = await finalizeImportHistoryReview({
        sourceNoteId: selectedGroup.sourceNoteId,
        decisions: buildReviewDecisions(selectedDrafts),
      });
      notifyTimelineRefresh([selectedGroup.sourceNoteId], Number(result.savedCount || 0));
      if (reviewSources.length === 1) onClose?.({ complete: true, savedCount: result.savedCount, results: [result] });
    } catch (saveError) {
      setError(saveError?.message || "We couldn't add these entries yet.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : () => onClose?.()}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { m: { xs: 0, sm: 4 }, width: { xs: "100%", sm: "calc(100% - 64px)" }, height: { xs: "100%", sm: "auto" }, maxHeight: { xs: "100%", sm: "92vh" }, borderRadius: { xs: 0, sm: 3 } } }}
    >
      <DialogTitle sx={{ px: { xs: 2, sm: 3 }, pt: 2.25, pb: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Typography component="span" sx={{ display: "block", fontSize: "1.12rem", fontWeight: 900 }}>Review imported history</Typography>
          <Button onClick={() => onClose?.()} disabled={saving} sx={{ flexShrink: 0, p: 0, minWidth: 0, textTransform: "none", fontWeight: 800 }}>
            Back to Import History
          </Button>
        </Stack>
        <Typography component="span" sx={{ color: colors.semantic.ink2, fontSize: "0.78rem" }}>{groups.length} {groups.length === 1 ? "file" : "files"} · {candidates.length} entries found · {reviewedSourceIds.size} reviewed</Typography>
        <Typography component="span" sx={{ display: "block", mt: 0.5, color: colors.semantic.ink2, fontSize: "0.76rem" }}>
          Your imported files are saved. You can return and continue reviewing later.
        </Typography>
      </DialogTitle>
      {missingDateCount ? <Alert severity="warning" sx={{ borderRadius: 0 }}>{missingDateCount} {missingDateCount === 1 ? "entry has" : "entries have"} no date. They are excluded unless you add one.</Alert> : null}
      {error ? <Alert severity="warning" sx={{ borderRadius: 0 }}>{error}</Alert> : null}
      {includedMissingDate ? <Alert severity="info" sx={{ borderRadius: 0 }}>Add a date to every included entry before saving.</Alert> : null}
      <DialogContent dividers sx={{ p: 0, bgcolor: colors.semantic.card }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "260px minmax(0, 1fr)", lg: "260px minmax(0, 1fr) 280px" }, minHeight: { sm: 540 } }}>
          <Box sx={{ display: { xs: mobileDetailOpen ? "none" : "block", sm: "block" }, p: 1.5, borderRight: { sm: `1px solid ${colors.semantic.line}` }, overflowY: "auto" }}>
            <Typography sx={{ mb: 1, color: colors.semantic.ink2, fontSize: "0.7rem", fontWeight: 850, letterSpacing: "0.08em", textTransform: "uppercase" }}>Your files</Typography>
            <ImportHistoryReviewFileList groups={groups} drafts={drafts} reviewedSourceIds={reviewedSourceIds} selectedSourceId={selectedGroup?.sourceNoteId} onSelect={selectSource} />
          </Box>
          <Box sx={{ display: { xs: mobileDetailOpen ? "block" : "none", sm: "block" }, p: { xs: 1.5, sm: 2 }, overflowY: "auto" }}>
            <Button onClick={() => setMobileDetailOpen(false)} sx={{ display: { xs: "inline-flex", sm: "none" }, mb: 1, px: 0, textTransform: "none" }}>← All files</Button>
            {selectedGroup ? <ImportHistoryReviewGroup group={selectedGroup} drafts={selectedDrafts} updateDraft={updateDraft} onSelectedCandidateChange={setSelectedSourceContext} onReviewMedication={openMedicationReview} existingMedications={existingMedications} /> : null}
            <Box sx={{ display: "grid", gap: 1, mt: 1.5 }}>
              <Button variant="contained" fullWidth onClick={addSelectedSource} disabled={saving || selectedDrafts.some((draft) => draft.include && (!draft.localDate || (draft.dateYearAssumed && !draft.dateConfirmed)))} sx={{ textTransform: "none", fontWeight: 850 }}>Add {selectedDrafts.filter((draft) => draft.include).length} entries from this file</Button>
              <Button fullWidth onClick={finishSelectedSource} disabled={saving} sx={{ textTransform: "none", fontWeight: 800 }}>Save review for later</Button>
            </Box>
          </Box>
          <Box sx={{ display: { xs: "none", lg: "block" }, p: 2, borderLeft: `1px solid ${colors.semantic.line}`, bgcolor: "background.paper", overflowY: "auto" }}>
            <Typography sx={{ color: colors.semantic.ink2, fontSize: "0.7rem", fontWeight: 850, letterSpacing: "0.08em", textTransform: "uppercase" }}>Original source text</Typography>
            <Typography sx={{ mt: 0.75, color: colors.semantic.ink2, fontSize: "0.78rem" }}>This is the text the selected entry came from.</Typography>
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: colors.semantic.sageMist }}>
              <Typography sx={{ whiteSpace: "pre-wrap", fontSize: "0.86rem", lineHeight: 1.65, color: colors.semantic.ink }}>
                {selectedSourceContext?.sourceText || "Select an entry to see its source text."}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: `1px solid ${colors.semantic.line}`, bgcolor: "background.paper" }}>
        <Button onClick={() => onClose?.()} disabled={saving} sx={{ textTransform: "none" }}>
          Return to Import History
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !reviewedSourceIds.size || includedMissingDate} sx={{ textTransform: "none", fontWeight: 850 }}>
          {saving ? "Adding..." : `Add ${reviewedIncludedCount} from reviewed files`}
        </Button>
      </DialogActions>
      <Dialog open={Boolean(medicationReviewSource)} onClose={() => setMedicationReviewSource(null)} fullWidth maxWidth="sm">
        <DialogTitle>Review medication</DialogTitle>
        <DialogContent dividers>
          {medicationReviewSource ? (
            <MedicationSourceReview
              source={medicationReviewSource}
              existingMedications={existingMedications}
              open
              createTimelineEvent={false}
              reviewMissingActiveMedications={false}
              onError={setError}
              onMedicationListChanged={onMedicationListChanged}
            />
          ) : null}
        </DialogContent>
        <DialogActions><Button onClick={() => setMedicationReviewSource(null)}>Back to entry</Button></DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ImportHistoryReviewSheet;
